<template>
  <div class="attach-image-container" :class="computedClasses">

    <template v-if="link">
      <template v-if="uploadType === 'image'">
        <div
          class="attach-image-view"
          :style="{ backgroundImage: `url(${link})` }"
          @click="onClickToImage"
        />

        <button
          class="attach-remove-button"
          @click.prevent="onResetImage"
        >
          <i class="attach-remove-icon"></i>
        </button>
      </template>

      <template v-else>
        <button
          class="attach-image-label attach-image-label-reset"
          @click.prevent="onResetImage"
        >
          <base-icon name="basic_trash" />
        </button>
      </template>
    </template>

    <template v-else-if="loading">
      <div class="attach-image-spinner">
        <cs-spinner />
      </div>
    </template>

    <template v-else>
      <label
        :for="inputId"
        class="attach-image-label"
        :class="{ 'attach-image-label-ticket': uploadType === 'ticket' }"
      />
    </template>

    <input
      :id="inputId"
      ref="files"
      type="file"
      :accept="accept"
      @change="onChangeImage"
    />

  </div>
</template>

<script>
import { inArray } from 'lib/component/validators';

import HTTPService from '@/services/Http';
import logrocket from '@/services/logrocket';

const LIMIT_SIZE = 5;

export default {
  name: 'AttachmentImage',
  props: {
    // TODO: сделать тип composite значением по умолчанию
    type: {
      type: String,
      validator: inArray(['primary', 'secondary', 'composite']),
      default() {
        return 'primary';
      },
    },
    inputId: String,
    link: String,
    composite: Boolean,
    compositeTextarea: Boolean,
    accept: {
      type: String,
      default: 'image/*',
    },
    uploadType: {
      type: String,
      default: 'image',
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    computedClasses() {
      return {
        [this.type]: true,
        composite: this.composite,
        ['composite-textarea']: this.compositeTextarea,
      };
    },
  },
  watch: {
    loading: {
      handler(val) {
        this.$emit('loading', val);
      },
    },
  },
  methods: {
    isInvalidSize(file) {
      const fileSize = file.size / 1024 / 1024;

      return fileSize > LIMIT_SIZE;
    },
    onResetImage() {
      this.$refs.files.value = '';

      if (!/safari/i.test(navigator.userAgent)) {
        this.$refs.files.type = '';
        this.$refs.files.type = 'file';
      }
      this.$emit('reset');
    },
    onChangeImage($e) {
      const files = $e.target.files || $e.dataTransfer.files;

      if (!files.length) {
        return;
      }

      if (this.isInvalidSize(files[0])) {
        this.$alert({
          type: 'warning',
          text: this.$t('notification.upload.file.size.error'),
        });

        return;
      }

      this.uploadImage(files[0]);
    },
    async uploadImage(file) {
      let formData = new FormData();
      formData.append('file', file);

      try {
        this.loading = true;
        const { data } = await HTTPService.upload(formData, this.uploadType);

        this.$emit('uploaded', data);
      } catch (error) {
        logrocket.logger(error);

        this.$alert({
          type: 'warning',
          text: this.$t('notification.upload.file.error'),
        });
      } finally {
        this.loading = false;
      }
    },
    async onClickToImage() {
      await this.$nextTick();

      this.$refs.files.click();
    },
  },
};
</script>

<style lang="sass" scoped>
  img
    width: 100%

  input[type=file]
    display: none

  .attach-image-container
    width: 60px
    height: 60px
    position: relative
    display: flex
    justify-content: center
    align-items: center

    +respond-to('medium')
      &:hover
        .attach-remove-button
          opacity: 1

    &.primary
      +respond-to('medium')
        width: 100px
        height: 100px

    &.secondary
      +respond-to('medium')
        width: 80px
        height: 80px

    &.composite
      width: 80px
      height: 80px
      align-self: flex-start

    &.composite-textarea
      width: 60px
      height: 60px
      position: absolute
      top: 10px
      right: 10px
      z-index: 100

  .attach-image-label
    width: 100%
    height: 100%
    background:
      repeat: no-repeat
      position: center
    overflow: hidden
    transition: all .2s
    cursor: pointer

    #{'.primary'} &
      border-radius: 50px

    #{'.secondary'} &
      border-radius: 8px 8px 8px 0px

    #{theme('eclipse')} &
      background:
        color: #D0D0D0
        image: url('~@/assets/svg/eclipse/plus-camera.svg')

      &:hover
        background-color: #DDD

    #{theme('obsidian')} &
      background:
        color: #181722
        image: url('~@/assets/svg/obsidian/plus-camera.svg')

      &:hover
        background-color: rgba(40, 41, 61, 0.75)

    #{'.composite'} &
      border-radius: 0px 8px 8px 0px
      background-color: #131212

      &:hover
        background-color: rgba(40, 41, 61, 0.75)

    #{'.composite-textarea'} &
      border-radius: 5px

  .attach-image-label-ticket
    #{theme('obsidian')} &
      background:
        color: #181722
        image: url('~@/assets/svg/obsidian/paperclip.svg')

  .attach-image-label-reset
    +flexbox
    +align-items(center)
    +justify-content(center)

    #{theme('obsidian')} &
      background-image: none

  .attach-image-spinner
    width: 100%
    height: 100%
    display: flex
    overflow: hidden
    cursor: wait

    #{'.primary'} &
      border-radius: 50px

    #{'.secondary'} &
      border-radius: 8px 8px 8px 0px

    #{'.composite'} &
      border-radius: 0px 8px 8px 0px

    #{'.composite-textarea'} &
      border-radius: 5px

    #{theme('eclipse')} &
      background-color: #D0D0D0

    #{theme('obsidian')} &
      background-color: #181722

  .attach-image-view
    width: 100%
    height: 100%
    position: relative
    background:
      repeat: no-repeat
      position: center
      size: cover
    image-orientation: from-image
    overflow: hidden
    cursor: pointer

    &:hover
      &:after
        background-color: rgba(255, 255, 255, .15)

    &:after
      content: ''
      position: absolute
      width: 100%
      height: 100%
      top: 0
      left: 0
      background-color: transparent
      transition: background-color .2s

    #{'.primary'} &
      border-radius: 50px

    #{'.secondary'} &
      border-radius: 8px 8px 8px 0px

    #{'.composite'} &
      border-radius: 0px 8px 8px 0px

    #{'.composite-textarea'} &
      border-radius: 5px

  .attach-remove-button
    display: flex
    position: absolute
    padding: 10px
    background-color: transparent
    transition: opacity .2s
    cursor: pointer

    #{'.primary'} &
      top: -15px
      right: -15px

      +respond-to('medium')
        top: -10px
        right: -10px

    #{'.secondary'} &
      top: -20px
      right: -20px

    +respond-to('medium')
      opacity: 0

  .attach-remove-icon
    width: 20px
    height: 20px
    background:
      repeat: no-repeat
      position: center
      size: 14px 14px
    border-radius: 50%

    #{theme('eclipse')} &
      background:
        color: #FFF
        image: url('~@/assets/svg/eclipse/close.svg')

    #{theme('obsidian')} &
      background:
        color: #27283C
        image: url('~@/assets/svg/obsidian/close.svg')
</style>
